import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSession } from 'src/contexts/SessionContext';
import {
  FranchiseDataParams,
  FranchiseFee,
  FranchiseFeesQueryKey,
  createFranchiseFee as createFee,
  deleteFranchiseFee as deleteFee,
  fetchFranchiseFee,
  fetchFranchiseFees,
  fetchFranchiseFeesQueryKey,
  updateFranchiseFee as updateFee,
} from 'src/models/hq/franchise-fee/franchise-fee';
import { useListSearchParams } from 'src/utils/useListSearchParams';

export const useFranchiseFeePage = (id?: string) => {
  const { session: currentPlatform, trainer: currentTrainer } = useSession();
  const queryClient = useQueryClient();

  const {
    params,
    updateParam,
    filterParams,
    updateFilterParam,
    getFilterParamValue,
    resetFilterParams,
  } = useListSearchParams({
    paramPrefix: 'franchise_fee_',
    defaultPerPage: 20,
  });
  const {
    data: fees = { franchiseFees: [], total: 0 },
    isLoading,
    isError,
  } = useQuery({
    queryKey: fetchFranchiseFeesQueryKey({
      page: params.page,
      perPage: params.perPage,
      search: params.search,
      sortBy: params.sortBy,
      sortDir: params.sortDir,
      filters: {
        feeType: filterParams.feeType,
        startDateRange: filterParams.startDateRange,
        endDateRange: filterParams.endDateRange,
        active: filterParams.active,
      },
    }),
    queryFn: () =>
      fetchFranchiseFees({
        page: params.page,
        perPage: params.perPage,
        search: params.search,
        sortBy: params.sortBy,
        sortDir: params.sortDir,
        filters: {
          feeType: filterParams.feeType,
          startDateRange: filterParams.startDateRange,
          endDateRange: filterParams.endDateRange,
          active: filterParams.active,
        },
      }),
  });

  // Fetch single franchise fee for edit page
  const {
    data: franchiseFee,
    isLoading: franchiseFeeIsLoading,
    isError: franchiseFeeIsError,
  } = useQuery({
    queryKey: ['franchise-fee', id],
    queryFn: async (): Promise<FranchiseFee> => {
      const data = await fetchFranchiseFee(id || '');
      return data;
    },
    enabled: !!id,
  });

  // Create mutation
  const createFranchiseFee = useMutation({
    mutationFn: async (data: FranchiseDataParams) => {
      const response = await createFee(data);
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [FranchiseFeesQueryKey] });
    },
  });

  // Update mutation
  const updateFranchiseFee = useMutation({
    mutationFn: async ({
      id,
      ...data
    }: Partial<FranchiseFee> & { id: string }) => {
      const response = await updateFee(id, data as FranchiseDataParams);
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [FranchiseFeesQueryKey] });
      queryClient.invalidateQueries({ queryKey: ['franchise-fee', id] });
    },
  });

  // Delete mutation
  const deleteFranchiseFee = useMutation({
    mutationFn: async (id: string) => {
      const data = await deleteFee(id);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [FranchiseFeesQueryKey] });
    },
  });

  return {
    fees,
    feesIsLoading: isLoading,
    feesIsError: isError,
    franchiseFee,
    franchiseFeeIsLoading,
    franchiseFeeIsError,
    session: currentPlatform,
    trainer: currentTrainer,
    createFranchiseFee,
    updateFranchiseFee,
    deleteFranchiseFee,
    params,
    updateParam,
    filterParams,
    updateFilterParam,
    getFilterParamValue,
    resetFilterParams,
  };
};
